/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { sigintDeleteSignal } from "../funcs/sigintDeleteSignal.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SigintSignal } from "../models/components/sigintsignal.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DeleteSigintSignalRequest,
  DeleteSigintSignalSecurity,
} from "../models/operations/deletesigintsignal.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DeleteSigintSignalMutationVariables = {
  request: DeleteSigintSignalRequest;
  security?: DeleteSigintSignalSecurity | undefined;
  options?: RequestOptions;
};

export type DeleteSigintSignalMutationData = SigintSignal;

export type DeleteSigintSignalMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * deleteSignal sigint
 *
 * @remarks
 * Soft-delete a custom signal and detach it from every sensor in the selected project. Surviving sensor signal order is compacted; sensors may remain incomplete drafts.
 */
export function useDeleteSigintSignalMutation(
  options?: MutationHookOptions<
    DeleteSigintSignalMutationData,
    DeleteSigintSignalMutationError,
    DeleteSigintSignalMutationVariables
  >,
): UseMutationResult<
  DeleteSigintSignalMutationData,
  DeleteSigintSignalMutationError,
  DeleteSigintSignalMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDeleteSigintSignalMutation(client, options),
    ...options,
  });
}

export function mutationKeyDeleteSigintSignal(): MutationKey {
  return ["@gram/client", "sigint", "deleteSignal"];
}

export function buildDeleteSigintSignalMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DeleteSigintSignalMutationVariables,
  ) => Promise<DeleteSigintSignalMutationData>;
} {
  return {
    mutationKey: mutationKeyDeleteSigintSignal(),
    mutationFn: function deleteSigintSignalMutationFn({
      request,
      security,
      options,
    }): Promise<DeleteSigintSignalMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(sigintDeleteSignal(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
